
    import __i18nConfig from '@next-translate-root/i18n'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import { GetStaticPropsContext } from 'next/types';
import { Makeswift, Page as MakeswiftPage, PageProps as MakeswiftPageProps } from '@makeswift/runtime/next';
import { SWRConfig } from 'swr';
import { ArticlesDocument } from '@/generated/contentful';
import { client as contentful } from '@/lib/contentful/client';
import { serializeCacheKey } from '@/lib/contentful/utils';
import { getPageSnapshot } from '@/lib/makeswift/client';
import '@/lib/makeswift/components';
import { isNonNullable } from '@/utils/isNonNullable';
import { ArticleMeta } from '@/components/Contentful/entries/Article/ArticleMeta';
import { PreviewToast } from '@/components/PreviewToast';
type Props = {
    fallback: {
        [key: string]: unknown;
    };
    preview: boolean;
} & MakeswiftPageProps;
export default function Page({ snapshot, fallback, preview }: Props) {
    return (<SWRConfig value={{ fallback }}>
			<PreviewToast preview={preview}/>
			<ArticleMeta>
				<MakeswiftPage snapshot={snapshot}/>
			</ArticleMeta>
		</SWRConfig>);
}
async function getStaticProps({ previewData, preview, params, locale }: GetStaticPropsContext<{
    slug: string[];
}, {
    makeswift: boolean;
}>) {
    const slug = params?.slug?.at(-1) ?? '';
    const isMakeSwiftPreview = previewData?.makeswift == true;
    const article = await contentful({ preview: Boolean(preview) }).request(ArticlesDocument, {
        where: {
            slug,
            ...(isMakeSwiftPreview ? {} : { editStatus: '🟢 live' })
        },
        locale,
        limit: 1,
        preview: Boolean(preview)
    });
    // If no article found for slug, return 404
    if (article?.articleCollection?.items &&
        article?.articleCollection?.items?.length <= 0 &&
        !isMakeSwiftPreview) {
        console.warn(`No article found for slug ${slug} and locale ${locale}`);
        return { notFound: true, revalidate: 3600 };
    }
    // If child article is requested without parent article slug in URL, return 404
    if (params && params.slug.length === 1) {
        return { notFound: true, revalidate: 3600 };
    }
    // if parent article slug does not match the parent article slug in the URL, return 404
    if (params && article?.articleCollection?.items?.[0]?.parentArticle?.slug !== params.slug[0]) {
        return { notFound: true, revalidate: 3600 };
    }
    const snapshot = await getPageSnapshot({
        pathname: '/articles/child/__template__',
        isMakeswiftPreview: previewData?.makeswift == true,
        previewData,
        locale,
        timeout: {
            ms: 10000,
            message: `Timeout request getPageSnapshot for "/articles/child/__template__" – Locale: ${locale}`
        }
    });
    if (snapshot == null) {
        console.warn(`Could not get snapshot for "/articles/child/__template__"`);
        return { notFound: true, revalidate: 3600 };
    }
    return {
        props: {
            snapshot,
            fallback: {
                [serializeCacheKey('article', { slug, locale })]: article
            },
            previewMode: Makeswift.getPreviewMode(previewData),
            preview: Boolean(preview),
            pageId: article?.articleCollection?.items?.[0]?.sys?.id ?? null
        },
        revalidate: 3600
    };
}
export async function getStaticPaths() {
    if (Boolean(process.env.SKIP_BUILD_STATIC_GENERATION)) {
        return {
            paths: [],
            fallback: 'blocking'
        };
    }
    const { articleCollection } = await contentful({ preview: false }).request(ArticlesDocument, {
        where: { parentArticle_exists: true, editStatus: '🟢 live' },
        limit: 5,
        locale: 'en-US'
    });
    return {
        paths: Array.from(new Set([
            ...(articleCollection?.items ?? [])
                .map((entry) => {
                if (!entry?.slug || !entry.parentArticle?.slug)
                    return null;
                return { params: { slug: [entry.parentArticle.slug, entry.slug] } };
            })
                .filter(isNonNullable)
        ])),
        fallback: 'blocking'
    };
}

    async function __Next_Translate__getStaticProps__1966e1dc2ef__(ctx) {
      const res = await getStaticProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/articles/[...slug]',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || (() => Promise.resolve({})),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__1966e1dc2ef__ as getStaticProps }
  